/**
 * Workspaces administration surface.
 *
 * Visibility / capabilities (enforced server-side, mirrored here for UX):
 * - Superadmin (is_superuser): sees and manages every workspace, can create,
 *   delete and reassign ownership.
 * - Admin role / workspace admin / owner: manages workspaces they own or
 *   administer (name, description, members, admins). Admins can also create
 *   workspaces.
 * - Everyone else: read-only view of workspaces they belong to.
 *
 * The server returns a `can_manage` flag per workspace which drives the edit UI.
 * @component
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import api, { MEDIA_URL } from '../../services/api';
import useCurrentUser from '../../utils/useCurrentUser';

const resolveImage = (image) =>
  !image ? undefined : image.startsWith('http') ? image : `${MEDIA_URL}${image}`;

const userLabel = (u) => (u ? u.full_name || u.username || u.email : '');

const userInitials = (u) => {
  const source = userLabel(u);
  return source
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
};

const UserAvatar = ({ user, size = 28 }) => (
  <Avatar
    src={resolveImage(user?.image)}
    sx={{ width: size, height: size, fontSize: size * 0.42 }}
  >
    {userInitials(user)}
  </Avatar>
);

/** Edit/create dialog. `workspace` null => create mode. */
const WorkspaceDialog = ({
  open,
  onClose,
  onSave,
  workspace,
  users,
  isSuperAdmin,
  saving,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [owner, setOwner] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [members, setMembers] = useState([]);

  const byId = useMemo(() => {
    const map = new Map(users.map((u) => [u.id, u]));
    return map;
  }, [users]);

  useEffect(() => {
    if (!open) return;
    if (workspace) {
      setName(workspace.name || '');
      setDescription(workspace.description || '');
      setOwner(workspace.owner_detail || byId.get(workspace.owner) || null);
      setAdmins(
        workspace.admins_detail ||
          (workspace.admins || []).map((id) => byId.get(id)).filter(Boolean)
      );
      setMembers(
        workspace.members_detail ||
          (workspace.members || []).map((id) => byId.get(id)).filter(Boolean)
      );
    } else {
      setName('');
      setDescription('');
      setOwner(null);
      setAdmins([]);
      setMembers([]);
    }
  }, [open, workspace, byId]);

  const handleSubmit = () => {
    const payload = {
      name: name.trim(),
      description: description.trim(),
      admins: admins.map((u) => u.id),
      members: members.map((u) => u.id),
    };
    // Owner is only assignable/reassignable by a superadmin.
    if (isSuperAdmin && owner) {
      payload.owner = owner.id;
    }
    onSave(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{workspace ? 'Edit workspace' : 'New workspace'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ mt: 0.5 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          {isSuperAdmin && (
            <Autocomplete
              options={users}
              value={owner}
              onChange={(_, v) => setOwner(v)}
              getOptionLabel={userLabel}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => (
                <TextField {...params} label="Owner" placeholder="Select owner" />
              )}
            />
          )}
          <Autocomplete
            multiple
            options={users}
            value={admins}
            onChange={(_, v) => setAdmins(v)}
            getOptionLabel={userLabel}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => (
              <TextField {...params} label="Admins" placeholder="Add admins" />
            )}
          />
          <Autocomplete
            multiple
            options={users}
            value={members}
            onChange={(_, v) => setMembers(v)}
            getOptionLabel={userLabel}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => (
              <TextField {...params} label="Members" placeholder="Add members" />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || !name.trim()}
        >
          {saving ? 'Saving…' : workspace ? 'Save changes' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const WorkspaceCard = ({ workspace, onEdit, onDelete, canDelete }) => {
  const admins = workspace.admins_detail || [];
  const members = workspace.members_detail || [];
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar variant="rounded" sx={{ bgcolor: 'primary.main' }}>
              <BusinessIcon />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {workspace.name}
              </Typography>
              {workspace.owner_detail && (
                <Typography variant="caption" color="text.secondary">
                  Owner: {userLabel(workspace.owner_detail)}
                </Typography>
              )}
            </Box>
          </Stack>
          {workspace.can_manage && (
            <Stack direction="row">
              <Tooltip title="Edit workspace">
                <IconButton size="small" onClick={() => onEdit(workspace)}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {canDelete && (
                <Tooltip title="Delete workspace">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDelete(workspace)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          )}
        </Stack>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1.5, minHeight: 40 }}
        >
          {workspace.description || 'No description.'}
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        <Stack spacing={1.25}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Admins ({admins.length})
            </Typography>
            {admins.length ? (
              <AvatarGroup
                max={6}
                sx={{ justifyContent: 'flex-start', mt: 0.5 }}
                slotProps={{ additionalAvatar: { sx: { width: 28, height: 28 } } }}
              >
                {admins.map((u) => (
                  <Tooltip key={u.id} title={userLabel(u)}>
                    <Box>
                      <UserAvatar user={u} />
                    </Box>
                  </Tooltip>
                ))}
              </AvatarGroup>
            ) : (
              <Typography variant="body2" color="text.disabled">
                None
              </Typography>
            )}
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Members ({members.length})
            </Typography>
            {members.length ? (
              <AvatarGroup
                max={8}
                sx={{ justifyContent: 'flex-start', mt: 0.5 }}
                slotProps={{ additionalAvatar: { sx: { width: 28, height: 28 } } }}
              >
                {members.map((u) => (
                  <Tooltip key={u.id} title={userLabel(u)}>
                    <Box>
                      <UserAvatar user={u} />
                    </Box>
                  </Tooltip>
                ))}
              </AvatarGroup>
            ) : (
              <Typography variant="body2" color="text.disabled">
                None
              </Typography>
            )}
          </Box>
        </Stack>

        {workspace.can_manage && (
          <Chip
            label="You manage this"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ mt: 1.5 }}
          />
        )}
      </CardContent>
    </Card>
  );
};

const WorkplaceSettings = () => {
  const { isSuperAdmin, canCreateWorkspace } = useCurrentUser();
  const [workspaces, setWorkspaces] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    let active = true;
    Promise.all([api.get('workspaces/'), api.get('users/')])
      .then(([wsRes, usersRes]) => {
        if (!active) return;
        setWorkspaces(wsRes.data);
        setUsers(usersRes.data);
      })
      .catch(() => active && setError('Could not load workspaces.'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return workspaces;
    return workspaces.filter((w) =>
      [w.name, w.description].filter(Boolean).some((f) => f.toLowerCase().includes(q))
    );
  }, [workspaces, search]);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (workspace) => {
    setEditing(workspace);
    setDialogOpen(true);
  };

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editing) {
        const res = await api.patch(`workspaces/${editing.id}/`, payload);
        setWorkspaces((list) =>
          list.map((w) => (w.id === editing.id ? res.data : w))
        );
        setToast({ severity: 'success', message: 'Workspace updated.' });
      } else {
        const res = await api.post('workspaces/', payload);
        setWorkspaces((list) => [...list, res.data]);
        setToast({ severity: 'success', message: 'Workspace created.' });
      }
      setDialogOpen(false);
      setEditing(null);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || 'Failed to save workspace. Please try again.';
      setToast({ severity: 'error', message: detail });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (workspace) => {
    if (
      !window.confirm(
        `Delete workspace “${workspace.name}”? This permanently removes it and all its data.`
      )
    ) {
      return;
    }
    try {
      await api.delete(`workspaces/${workspace.id}/`);
      setWorkspaces((list) => list.filter((w) => w.id !== workspace.id));
      setToast({ severity: 'success', message: 'Workspace deleted.' });
    } catch (err) {
      const detail =
        err?.response?.data?.detail || 'Failed to delete workspace.';
      setToast({ severity: 'error', message: detail });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Workspaces
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isSuperAdmin
              ? 'You can manage every workspace.'
              : 'Workspaces you own, administer, or belong to.'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <TextField
            size="small"
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 220 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {canCreateWorkspace && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
              sx={{ whiteSpace: 'nowrap' }}
            >
              New
            </Button>
          )}
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {filtered.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            color: 'text.secondary',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <BusinessIcon sx={{ fontSize: 40, opacity: 0.4 }} />
          <Typography sx={{ mt: 1 }}>
            {search ? `No workspaces match “${search}”.` : 'No workspaces yet.'}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filtered.map((w) => (
            <Grid item xs={12} sm={6} md={4} key={w.id}>
              <WorkspaceCard
                workspace={w}
                onEdit={openEdit}
                onDelete={handleDelete}
                canDelete={isSuperAdmin}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <WorkspaceDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
        onSave={handleSave}
        workspace={editing}
        users={users}
        isSuperAdmin={isSuperAdmin}
        saving={saving}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            variant="filled"
            onClose={() => setToast(null)}
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default WorkplaceSettings;
